import React, { createRef, useRef } from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { ColumnApi, GridReadyEvent, GridApi } from 'ag-grid-community';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-material.css';
import './ag-grid-overrides.css';
import { randomID } from '@starryui10/utils/stringUtil';

export interface IBasicGridRefs {
    gridApi: () => GridApi | undefined;
    columnApi: () => ColumnApi | undefined;
    autoWidth: () => void;
}

export interface IBasicGridProps extends AgGridReactProps {
    onGridHasLoaded?: () => void;
    onUserColumnInteraction?: () => void;
}

const PAGE_SIZE = 40;

export const BasicGrid = React.forwardRef<IBasicGridRefs, IBasicGridProps>((props, ref) => {
    const grid = useRef<GridReadyEvent>();
    const container = createRef<HTMLDivElement>();

    React.useImperativeHandle(ref, () => ({
        columnApi: () => grid?.current?.columnApi,
        gridApi: () => grid?.current?.api,
        autoWidth: resizeContainer,
    }));

    const resizeContainer = () => {
        if (container.current && grid.current) {
            let totalColsWidth = 0;
            const allColumns = grid.current?.columnApi.getAllColumns();
            if (allColumns) {
                for (let i = 0; i < allColumns.length; i++) {
                    const column = allColumns[i];
                    if (column.isVisible()) {
                        totalColsWidth += column.getActualWidth();
                    }
                }
            }
            container.current.style.width = `${totalColsWidth}px`;
        }
    };

    const handleGridReady = (event: GridReadyEvent) => {
        grid.current = event;
        if (props.onGridHasLoaded) {
            props.onGridHasLoaded();
        }
    };

    const handleColumnInteraction = () => {
        if (props.onUserColumnInteraction) {
            props.onUserColumnInteraction();
        }
    };

    return (
        <div ref={container} id={`ui-grid-${randomID()}`} className="sr-sticky-controls-v2">
            <div className="sr-overrides ag-theme-material" style={{ height: '100%' }}>
                <AgGridReact
                    defaultColDef={{ resizable: true, sortable: true }}
                    getRowNodeId={(d) => d.id}
                    stopEditingWhenGridLosesFocus
                    paginationPageSize={PAGE_SIZE}
                    {...props}
                    onGridReady={handleGridReady}
                    onColumnMoved={handleColumnInteraction}
                    onColumnResized={handleColumnInteraction}
                    onColumnVisible={handleColumnInteraction}
                    suppressDragLeaveHidesColumns
                />
            </div>
        </div>
    );
});
